import "../Css/StickyNav.css";
import { useState, useEffect, useRef } from 'react';
import { Navbar, Nav } from 'react-bootstrap';

const StickyNav = () => {
    const [isSticky, setIsSticky] = useState(false);
    const [activeSection, setActiveSection] = useState('');
    const [clickedSection, setClickedSection] = useState(null);
    const navRef = useRef(null);
    const navContainerRef = useRef(null);
    const navItemRefs = useRef({});
    const isScrollingRef = useRef(false);

    // Handle scroll visibility and active section
    useEffect(() => {
        const handleScroll = () => {
            const offset = window.scrollY;
            setIsSticky(offset > 100);

            const navHeight = navRef.current?.offsetHeight || 70;
            const viewportHeight = window.innerHeight;

            // If a section was clicked and scroll is in progress or just finished
            if (clickedSection) {
                const element = document.getElementById(clickedSection);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    // Lock activeSection to clickedSection during scroll
                    if (isScrollingRef.current) {
                        if (activeSection !== clickedSection) {
                            setActiveSection(clickedSection);
                        }
                        // Check if scroll has settled near the target
                        if (Math.abs(rect.top - (navHeight + 20)) < 50) {
                            isScrollingRef.current = false; // Scroll complete
                        }
                        return;
                    }
                    // After scroll settles, keep clickedSection active unless it's out of view
                    if (rect.bottom > navHeight && rect.top < viewportHeight) {
                        if (activeSection !== clickedSection) {
                            setActiveSection(clickedSection);
                        }
                        return;
                    } else {
                        setClickedSection(null); // Clear clickedSection if out of view
                    }
                }
            }

            // Normal scroll-based active section detection
            if (!isScrollingRef.current) {
                const sections = navItems.map(navItem => navItem.id)
                let newActiveSection = '';

                for (const section of sections) {
                    const element = document.getElementById(section);
                    if (element) {
                        const rect = element.getBoundingClientRect();
                        if (rect.top <= viewportHeight / 2 && rect.bottom > navHeight) {
                            newActiveSection = section;
                            break;
                        }
                    }
                }

                if (newActiveSection && newActiveSection !== activeSection) {
                    console.log("Setting new active section:", newActiveSection);
                    setActiveSection(newActiveSection);
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Initial check on mount
        return () => window.removeEventListener('scroll', handleScroll);
    }, [activeSection, clickedSection]);

    // Scroll nav container to active item
    useEffect(() => {
        if (activeSection && navContainerRef.current && navItemRefs.current[activeSection]) {
            const activeItem = navItemRefs.current[activeSection];
            const container = navContainerRef.current;

            const containerWidth = container.offsetWidth;
            const itemWidth = activeItem.offsetWidth;
            const itemOffsetLeft = activeItem.offsetLeft;
            const scrollPosition = itemOffsetLeft - (containerWidth / 2) + (itemWidth / 2);

            const maxScroll = container.scrollWidth - containerWidth;
            const finalScrollPosition = Math.max(0, Math.min(scrollPosition, maxScroll));

            container.scrollTo({
                left: finalScrollPosition,
                behavior: 'smooth'
            });
        }
    }, [activeSection]);

    // Smooth scroll to section with offset and set as active
    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const navHeight = navRef.current?.offsetHeight || 70;
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - navHeight - 20;

            isScrollingRef.current = true;
            setClickedSection(sectionId);
            setActiveSection(sectionId);

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    const navItems = [
        { id: 'header', label: 'Profile' },
        { id: 'about', label: 'About' },
        { id: 'experience', label: 'Experience' },
        { id: 'education', label: 'Education' },
        { id: 'skills', label: 'Skills' },
        { id: 'certifications', label: 'Certifications' },
        { id: 'languages', label: 'Languages' },
        { id: 'hobbies', label: 'Hobbies' },
        { id: 'references', label: 'References'}
    ];

    return (
        <Navbar
            ref={navRef}
            fixed="top"
            className={`sticky-nav ${isSticky ? 'visible' : ''}`}
            variant="dark"
        >
            <div className="nav-container" ref={navContainerRef}>
                <Nav className="nav-scroll">
                    {navItems.map((item) => (
                        <Nav.Link
                            key={item.id}
                            ref={(el) => (navItemRefs.current[item.id] = el)}
                            className={activeSection === item.id ? 'active' : ''}
                            onClick={() => scrollToSection(item.id)}
                        >
                            {item.label}
                        </Nav.Link>
                    ))}
                </Nav>
            </div>
        </Navbar>
    );
};

export default StickyNav;